import { Box, Button, Container, Heading, HStack, Image, SimpleGrid, Spinner, Stack, Text } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '../../app-shell/AppShell';
import { catalogCoverImage } from '../../catalog/assets';
import { FavouriteButton } from '../../favourites/FavouriteButton';
import { listFavourites, FavouritesPage as FavouritesPagePayload } from '../../favourites/api';
import { useAuth } from '../../auth/AuthProvider';

export default function FavouritesPage() {
  const { t } = useTranslation();
  const { authenticated } = useAuth();
  const [data, setData] = useState<FavouritesPagePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authenticated) return;
    let cancelled = false;
    setLoading(true);
    listFavourites(1, 60)
      .then((p) => {
        if (!cancelled) setData(p);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Failed to load favourites');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authenticated]);

  return (
    <AppShell>
      <Container maxW="1100px" py={8}>
        <Heading size="lg" color="whiteAlpha.900" mb={6}>
          {t('favourites.title', 'My favourites')}
        </Heading>

        {loading && (
          <HStack justify="center" py={12}>
            <Spinner color="pink.300" />
          </HStack>
        )}

        {!loading && error && (
          <Text color="red.300">{error}</Text>
        )}

        {!loading && !error && data && data.items.length === 0 && (
          <Text color="whiteAlpha.700">
            {t(
              'favourites.empty',
              "You haven't favourited any songs yet. Tap the heart on a song page to save it here.",
            )}
          </Text>
        )}

        {!loading && !error && data && data.items.length > 0 && (
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
            {data.items.map((f) => {
              const s = f.song;
              const thumb = s?.media?.songThumbnailImage;
              const songId = s?._id ? String(s._id) : undefined;
              const refHref = s
                ? `/generator?refTitle=${encodeURIComponent(s.songTitle || '')}&refArtist=${encodeURIComponent(s.artistName || '')}${songId ? `&ref=${encodeURIComponent(songId)}` : ''}`
                : '/generator';
              return (
                <Box
                  key={f._id}
                  bg="night.800"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  borderRadius="8px"
                  p={4}
                >
                  <HStack align="start" spacing={3}>
                    <Image
                      src={catalogCoverImage(thumb, 'song')}
                      alt={`${s?.songTitle} by ${s?.artistName}`}
                      boxSize="68px"
                      objectFit="cover"
                      borderRadius="8px"
                    />
                    <Stack spacing={1} flex="1" minW={0}>
                      <Text fontWeight={800} noOfLines={1} color="whiteAlpha.900">
                        {s?.songTitle}
                      </Text>
                      <Text fontSize="sm" color="whiteAlpha.700" noOfLines={1}>
                        {s?.artistName}
                      </Text>
                      <Button
                        as={Link}
                        to={refHref}
                        size="xs"
                        colorScheme="pink"
                        variant="outline"
                        mt={1}
                        data-cta="fav:generate-similar"
                      >
                        {t('favourites.generateSimilar', 'Generate similar')}
                      </Button>
                    </Stack>
                    <FavouriteButton songId={songId} initialFavourited size="md" />
                  </HStack>
                </Box>
              );
            })}
          </SimpleGrid>
        )}
      </Container>
    </AppShell>
  );
}
